import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  get as commsGet,
  post as commsPost,
  remove as commsRemove,
} from "../../../service/comms/commsApi";
import {
  getMessages,
  sendMessage,
  deleteMessage as deleteMessageApi,
  markRead,
  getReads,
  aiFeedback,
} from "../../../service/comms/messagesService";
import { getRoom, getParticipants } from "../../../service/comms/roomsService";
import { useRoomChannel } from "../../../hooks/comms/useRoomChannel";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { RealtimeContext } from "../../../hooks/comms/RealtimeProvider";
import { participantId } from "../../../utils/commsParticipantId";
import {
  sortBySeq,
  mergeMessages,
  maxRealSeq,
  minRealSeq,
  extractMessage,
} from "../../../utils/commsMessageMerge";
import {
  appendMessage as cacheAppendMessage,
  seedMessages as cacheSeedMessages,
  getCachedMessages,
} from "../../../hooks/comms/roomMessagesCache";

const PAGE_LIMIT = 50;
const TYPING_EXPIRE_MS = 6000;
const MARK_READ_DEBOUNCE_MS = 800;
const POLL_FALLBACK_MS = 8000;

/**
 * Generate a client-side idempotency key for optimistic sends.
 * @returns {string}
 */
const genKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `k_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

/**
 * Monotonically update a receipt field for a user (never regress).
 * @param {Array<Object>} prev
 * @param {string} userId
 * @param {string} field
 * @param {number} seq
 * @returns {Array<Object>}
 */
const bumpReceipt = (prev, userId, field, seq) => {
  const idx = prev.findIndex((r) => r.user_id === userId);
  if (idx === -1) {
    return [...prev, { user_id: userId, [field]: seq }];
  }
  const current = prev[idx];
  if ((current[field] || 0) >= seq) {
    return prev;
  }
  const next = [...prev];
  next[idx] = { ...current, [field]: seq };
  return next;
};

/**
 * Encapsulates all data loading, realtime wiring, optimistic sending and
 * receipt tracking for a single chat room. Consumed by <Thread />.
 *
 * @param {string} roomId
 * @param {string} viewerUserId
 * @returns {Object} Room state plus action handlers.
 */
export function useRoomMessages(roomId, viewerUserId) {
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [reads, setReads] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [room, setRoom] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [aiMode, setAiMode] = useState(null);
  const [toast, setToast] = useState(null);
  // Set when the room answers 403 — the caller is not a participant. For a
  // support ticket that just means "not picked up yet", so it is a state to
  // render (offer Pick up), not an error to toast, and every refetch/poll
  // stands down until the room changes.
  const [forbidden, setForbidden] = useState(false);
  // Bumped by retry(). Membership can change without the room changing —
  // picking a ticket up adds you to it — and the load below would otherwise
  // never run again for this room.
  const [reloadToken, setReloadToken] = useState(0);

  // Refs give realtime handlers access to the latest state without
  // resubscribing on every render.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const participantsRef = useRef(participants);
  participantsRef.current = participants;
  // The hook instance survives room switches (Thread isn't keyed by roomId),
  // so async callbacks must re-check this before touching state — otherwise a
  // slow response for the previous room corrupts the current thread.
  const roomIdRef = useRef(roomId);
  roomIdRef.current = roomId;
  // Read by the realtime refetch, which must not be re-created on every
  // change (that would resubscribe the room handlers).
  const forbiddenRef = useRef(forbidden);
  forbiddenRef.current = forbidden;

  const typingTimersRef = useRef({});
  const markTimerRef = useRef(null);

  const { getClientName, getCoachName } = useCommsIdentity();
  const { connected, connectionEpoch } = useContext(RealtimeContext);

  const notify = useCallback((message, type = "error") => {
    setToast({ message, type, ts: Date.now() });
  }, []);

  /** Resolve a display name for a user id from participants/room. */
  const nameFor = useCallback(
    (userId) => {
      const p = participantsRef.current.find((x) => participantId(x) === userId);
      const cachedName =
        p?.participant_role === "COACH" ? getCoachName(userId) : getClientName(userId);
      return p?.name || cachedName || (p ? `Participant ${userId}` : `User ${userId}`);
    },
    [getClientName, getCoachName]
  );

  // --- Initial load on room change ------------------------------------------
  useEffect(() => {
    if (!roomId) {
      return undefined;
    }
    let cancelled = false;

    // Reset for the new room — but hydrate messages from the global cache so
    // a previously visited (or background-updated) conversation renders
    // instantly while the authoritative fetch reconciles below.
    //
    // Only when the buffer is contiguous, though: a room flagged after a seq
    // gap (missed realtime events, or a websocket reconnect via markAllStale)
    // may be missing messages in the middle, and painting it would show a
    // thread with a hole in it until the fetch below lands. An empty pane for
    // that moment is honest; a plausible-looking thread with a gap isn't.
    const cached = getCachedMessages(roomId);
    setMessages(cached.contiguous ? cached.messages : []);
    setReads([]);
    setParticipants([]);
    setRoom(null);
    setTypingUsers({});
    setHasMore(false);
    setAiMode(null);
    setForbidden(false);

    const load = async () => {
      try {
        const [roomRes, partRes, msgRes, readsRes] = await Promise.all([
          getRoom(commsGet, roomId),
          getParticipants(commsGet, roomId, true),
          getMessages(commsGet, roomId, { limit: PAGE_LIMIT }),
          getReads(commsGet, roomId),
        ]);
        if (cancelled) {
          return;
        }
        const serverPage = msgRes?.messages || [];
        setRoom(roomRes || null);
        setAiMode(roomRes?.ai_mode ?? null);
        setParticipants(partRes?.participants || []);
        setMessages((prev) => mergeMessages(prev, serverPage));
        setHasMore(Boolean(msgRes?.has_more));
        setReads(readsRes?.reads || []);
        cacheSeedMessages(roomId, serverPage);
      } catch (err) {
        if (cancelled) {
          return;
        }
        // 403 = not a participant of this room. Toasting it would be wrong:
        // for a support ticket in the unassigned pool it is the expected
        // answer until the agent picks it up, and the consumer renders that
        // state instead.
        if (err?.status === 403) {
          setForbidden(true);
          return;
        }
        notify(err?.message || err?.response?.data?.message || "Failed to load conversation");
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [roomId, notify, reloadToken]);

  /**
   * Re-run the room load for the same room.
   *
   * Needed because room *membership* can change while the room id does not:
   * picking up a support ticket adds the agent to it, and without this the
   * pane would sit on its "not a participant" state until a page reload.
   */
  const retry = useCallback(() => setReloadToken((t) => t + 1), []);

  // Clear pending typing timers on unmount.
  useEffect(() => {
    const timers = typingTimersRef.current;
    return () => {
      Object.values(timers).forEach((t) => clearTimeout(t));
      clearTimeout(markTimerRef.current);
    };
  }, []);

  // --- Realtime handlers -----------------------------------------------------
  const scheduleTypingExpiry = useCallback((userId) => {
    clearTimeout(typingTimersRef.current[userId]);
    typingTimersRef.current[userId] = setTimeout(() => {
      setTypingUsers((prev) => {
        if (!(userId in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }, TYPING_EXPIRE_MS);
  }, []);

  const refetchNewest = useCallback(async () => {
    // The room already told us we're not a participant; asking again every
    // event or poll tick only fills the console with 403s.
    if (forbiddenRef.current) {
      return;
    }
    try {
      const res = await getMessages(commsGet, roomId, { limit: PAGE_LIMIT });
      const incoming = res?.messages || [];
      cacheSeedMessages(roomId, incoming);
      if (roomIdRef.current !== roomId) {
        return;
      }
      setMessages((prev) => mergeMessages(prev, incoming));
    } catch (err) {
      // Non-fatal: realtime refetch failed, will recover on next event.
    }
  }, [roomId]);

  const refetchParticipants = useCallback(async () => {
    try {
      const res = await getParticipants(commsGet, roomId, true);
      if (roomIdRef.current !== roomId) {
        return;
      }
      setParticipants(res?.participants || []);
    } catch (err) {
      // Non-fatal.
    }
  }, [roomId]);

  const handlers = {
    onMessage: (data) => {
      const msg = extractMessage(data);
      if (!msg) {
        // Thin/unknown payload — fall back to fetching the newest page.
        refetchNewest();
        return;
      }
      cacheAppendMessage(roomId, msg);
      const knownMax = maxRealSeq(messagesRef.current);
      setMessages((prev) => {
        // The realtime echo of our OWN send can beat the POST response;
        // drop the matching optimistic bubble so it doesn't render twice.
        let base = prev;
        if (String(msg.sender_id) === String(viewerUserId)) {
          const echoTempId = msg.idempotency_key
            ? `tmp_${msg.idempotency_key}`
            : null;
          // Fall back to content matching only when there IS content — two
          // media messages with empty captions would otherwise collapse.
          const echoContent =
            typeof msg.content === "string" && msg.content.trim()
              ? msg.content
              : null;
          const idx = prev.findIndex(
            (m) =>
              m._optimistic &&
              (m.id === echoTempId ||
                (echoContent !== null && m.content === echoContent))
          );
          if (idx !== -1) {
            base = prev.filter((_, i) => i !== idx);
          }
        }
        return mergeMessages(base, [msg]);
      });
      if (knownMax > 0 && msg.seq > knownMax + 1) {
        // Seq gap: messages were missed (e.g. brief disconnect) — reconcile.
        refetchNewest();
      }
    },
    // Carries no body, only which message changed. Refetching the newest page
    // picks the change up whenever that message is on it; an older one is
    // already dimmed locally by the card that replaced it.
    onMessageUpdated: () => {
      refetchNewest();
    },
    onRead: (data) => {
      if (!data?.user_id) return;
      setReads((prev) =>
        bumpReceipt(prev, data.user_id, "last_read_seq", data.up_to_seq || 0)
      );
    },
    onDelivered: (data) => {
      if (!data?.user_id) return;
      setReads((prev) =>
        bumpReceipt(
          prev,
          data.user_id,
          "last_delivered_seq",
          data.up_to_seq || 0
        )
      );
    },
    onTyping: (data) => {
      if (!data?.user_id || data.user_id === viewerUserId) return;
      setTypingUsers((prev) => {
        const next = { ...prev };
        if (data.started) {
          next[data.user_id] = nameFor(data.user_id);
        } else {
          delete next[data.user_id];
        }
        return next;
      });
      if (data.started) {
        scheduleTypingExpiry(data.user_id);
      }
    },
    onParticipant: () => {
      refetchParticipants();
    },
    onAiSettings: (data) => {
      if (data?.mode) {
        setAiMode(data.mode);
      }
      refetchNewest();
    },
    onFlag: () => {
      refetchNewest();
    },
  };

  const { canPublish, ready } = useRoomChannel(roomId, handlers);

  // --- Reconnect reconcile ----------------------------------------------------
  // Messages/read-receipts published while the socket was down were missed;
  // refetch once per (re)connect. Skip the first epoch observed on mount —
  // the initial-load effect already covers it.
  const epochRef = useRef(connectionEpoch);
  useEffect(() => {
    if (connectionEpoch === epochRef.current) {
      return;
    }
    epochRef.current = connectionEpoch;
    if (!roomId) {
      return;
    }
    refetchNewest();
    getReads(commsGet, roomId)
      .then((res) => {
        if (roomIdRef.current === roomId) {
          setReads(res?.reads || []);
        }
      })
      .catch(() => {});
  }, [connectionEpoch, roomId, refetchNewest]);

  // --- Polling fallback while realtime is down --------------------------------
  // The open thread otherwise depends entirely on websocket events; when the
  // socket is disconnected, poll the newest page so the conversation still
  // moves. Background tabs skip ticks (the global 45s unread resync covers
  // them) and polling stops as soon as the socket reconnects.
  useEffect(() => {
    if (!roomId || connected || forbidden) {
      return undefined;
    }
    const id = setInterval(() => {
      if (document.visibilityState === "visible") {
        refetchNewest();
      }
    }, POLL_FALLBACK_MS);
    return () => clearInterval(id);
  }, [roomId, connected, forbidden, refetchNewest]);

  // --- Actions ---------------------------------------------------------------
  const loadOlder = useCallback(async () => {
    if (loadingOlder || !hasMore) {
      return;
    }
    const oldest = minRealSeq(messagesRef.current);
    if (oldest === undefined) {
      return;
    }
    setLoadingOlder(true);
    try {
      const res = await getMessages(commsGet, roomId, {
        limit: PAGE_LIMIT,
        before_seq: oldest,
      });
      if (roomIdRef.current === roomId) {
        setMessages((prev) => mergeMessages(prev, res?.messages || []));
        setHasMore(Boolean(res?.has_more));
      }
    } catch (err) {
      notify(err?.message || "Failed to load older messages");
    } finally {
      setLoadingOlder(false);
    }
  }, [roomId, loadingOlder, hasMore, notify]);

  const send = useCallback(
    async (rawPayload) => {
      const idempotencyKey = rawPayload.idempotency_key || genKey();
      const payload = { ...rawPayload, idempotency_key: idempotencyKey };
      const tempId = `tmp_${idempotencyKey}`;
      const tempSeq = maxRealSeq(messagesRef.current) + 0.5;

      const temp = {
        id: tempId,
        room_id: roomId,
        seq: tempSeq,
        sender_id: viewerUserId,
        sender_role: "COACH",
        message_type: payload.coach_only ? 1 : 1,
        coach_only: Boolean(payload.coach_only),
        content: payload.content,
        content_type: payload.content_type,
        content_attributes: payload.content_attributes,
        ai_generated: false,
        retracted: false,
        status: "sending",
        created_at: new Date().toISOString(),
        // The outbound rows already carry blob_path/mime_type/media_kind, so
        // the optimistic bubble renders exactly like the server echo will.
        attachments: payload.attachments || [],
        reply_to_id: payload.reply_to_id,
        mentions: payload.mentions,
        _optimistic: true,
      };

      setMessages((prev) => sortBySeq([...prev, temp]));

      try {
        const res = await sendMessage(commsPost, roomId, payload);
        const serverMsg = res?.data?.message;
        if (serverMsg) {
          cacheAppendMessage(roomId, serverMsg);
        }
        if (roomIdRef.current === roomId) {
          setMessages((prev) => {
            const withoutTemp = prev.filter((m) => m.id !== tempId);
            if (!serverMsg) {
              return withoutTemp;
            }
            return mergeMessages(withoutTemp, [serverMsg]);
          });
        }
        return serverMsg;
      } catch (err) {
        if (roomIdRef.current === roomId) {
          setMessages((prev) =>
            prev.map((m) => (m.id === tempId ? { ...m, status: "failed" } : m))
          );
        }
        notify(err?.message || "Message failed to send");
        throw err;
      }
    },
    [roomId, viewerUserId, notify]
  );

  const markReadUpTo = useCallback(
    (seq) => {
      if (!seq || !roomId) {
        return;
      }
      clearTimeout(markTimerRef.current);
      markTimerRef.current = setTimeout(() => {
        markRead(commsPost, roomId, seq).catch(() => {});
      }, MARK_READ_DEBOUNCE_MS);
    },
    [roomId]
  );

  const deleteMessage = useCallback(
    async (messageId) => {
      try {
        await deleteMessageApi(commsRemove, roomId, messageId);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId ? { ...m, retracted: true } : m
          )
        );
      } catch (err) {
        notify(err?.message || "Failed to delete message");
      }
    },
    [roomId, notify]
  );

  const sendAiFeedback = useCallback(
    async (messageId, feedback) => {
      try {
        await aiFeedback(commsPost, roomId, messageId, feedback);
        notify("Feedback sent", "success");
      } catch (err) {
        notify(err?.message || "Failed to send feedback");
      }
    },
    [roomId, notify]
  );

  return {
    messages,
    hasMore,
    loadingOlder,
    reads,
    participants,
    room,
    typingUsers,
    aiMode,
    canPublish,
    ready,
    forbidden,
    retry,
    toast,
    send,
    loadOlder,
    markReadUpTo,
    deleteMessage,
    sendAiFeedback,
  };
}
